/**
 * Runtime Zod mirror of the attribute definitions (SPEC screen 2).
 *
 * Built from the product's ATTRIBUTE ASSIGNMENTS (`GET
 * /products/{id}/attributes` joined to `GET /attributes` definitions) at
 * editor load: `text` → string, `number` → decimal-STRING validation (the
 * value never touches a JS number — wire decimals stay strings per
 * SPEC-CONVENTIONS), `select` → membership in the attribute's options. Client
 * validation is a convenience only; the server's service-layer validation
 * stays the authority and its `errors[]` (`attribute_value_invalid`,
 * `attribute_unknown_code`) map back onto grid cells via CLAUDE-A's
 * problem+json form mapper.
 */
import { z } from "zod";

import { PRODUCT_STATUSES, PRODUCT_TYPES, VARIANT_STATUSES, type Attribute } from "./api";

/**
 * Canonical wire decimal string: optional sign, integer digits, optional
 * fraction. String-level check only — `Number()`/`parseFloat` on wire
 * decimals is a review-blocking defect, so none happens here.
 */
const DECIMAL_STRING_RE = /^[+-]?\d+(\.\d+)?$/;

export function isDecimalString(value: string): boolean {
  return DECIMAL_STRING_RE.test(value);
}

// ── Wire-scale canonicalization (string padding only, never Number()) ────

/** Loose user entry: digits with an optional single dot ("12", "12.5", ".5"). */
const LOOSE_DECIMAL_RE = /^(\d*)(?:\.(\d*))?$/;

/**
 * Canonicalize a user-entered decimal STRING to the exact wire scale by
 * string manipulation only. Returns null when the input is not a plain
 * non-negative decimal, carries more fractional digits than the wire scale,
 * or overflows the integer-digit budget. `"12.5"` → `"12.5000000000"` for a
 * 10-digit factor; `"1"` → `"1.000000"` for a 6-digit qty.
 */
export function toWireDecimal(
  value: string,
  fractionDigits: number,
  maxIntegerDigits: number,
): string | null {
  const match = LOOSE_DECIMAL_RE.exec(value.trim());
  if (match === null) return null;
  const rawInt = match[1] ?? "";
  const rawFrac = match[2] ?? "";
  if (rawInt === "" && rawFrac === "") return null;
  if (rawFrac.length > fractionDigits) return null;
  const intPart = rawInt.replace(/^0+(?=\d)/, "");
  const normalizedInt = intPart === "" ? "0" : intPart;
  if (normalizedInt.length > maxIntegerDigits) return null;
  return `${normalizedInt}.${rawFrac.padEnd(fractionDigits, "0")}`;
}

/**
 * CatalogConversionFactorString: `^(?:0|[1-9][0-9]{0,9})\.[0-9]{10}$`,
 * nonzero. Null when the entry cannot be canonicalized.
 */
export function toConversionFactorString(value: string): string | null {
  const canonical = toWireDecimal(value, 10, 10);
  if (canonical === null || canonical === "0.0000000000") return null;
  return canonical;
}

/** CatalogQuantityString: `^(?:0|[1-9][0-9]{0,13})\.[0-9]{6}$` (non-negative). */
export function toQuantityString(value: string): string | null {
  return toWireDecimal(value, 6, 14);
}

/** Zod schema for one attribute value; all values are optional strings. */
function attributeValueSchema(attribute: Attribute): z.ZodType<string | undefined> {
  switch (attribute.type) {
    case "number":
      return z
        .string()
        .refine(isDecimalString, {
          params: { i18nKey: "catalog.editor.invalidDecimal" },
          message: "Enter a decimal number",
        })
        .optional();
    case "select": {
      const options = attribute.options;
      return z
        .string()
        .refine((value) => options.includes(value), {
          params: { i18nKey: "catalog.editor.invalidOption" },
          message: "Not one of the allowed options",
        })
        .optional();
    }
    case "text":
      return z.string().optional();
  }
}

export type VariantAttributeValues = Record<string, string | undefined>;

/**
 * Object schema keyed by `attributes.code`. Unknown existing codes stay
 * opaque and round-trip unchanged: definitions arrive asynchronously and a
 * product.update-only user may not be allowed to read them. The server still
 * rejects genuinely unknown codes authoritatively with
 * `attribute_unknown_code`.
 */
export function buildVariantAttributesSchema(
  attributes: readonly Attribute[],
): z.ZodType<VariantAttributeValues> {
  const shape: Record<string, z.ZodType<string | undefined>> = {};
  for (const attribute of attributes) {
    shape[attribute.code] = attributeValueSchema(attribute);
  }
  return z.object(shape).catchall(z.string()) as z.ZodType<VariantAttributeValues>;
}

/** Drop empty/undefined values before the payload goes on the wire. */
export function compactAttributeValues(values: VariantAttributeValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [code, value] of Object.entries(values)) {
    if (value !== undefined && value !== "") out[code] = value;
  }
  return out;
}

// ── Whole-editor schema (Details + variant grid, SPEC screen 2) ──────────

export interface EditorVariantValues {
  /**
   * Server variant id ("" for new grid rows). Named `variantId` because RHF's
   * useFieldArray reserves `id` for its own row keys.
   */
  variantId: string;
  sku: string;
  status: (typeof VARIANT_STATUSES)[number];
  attribute_values: VariantAttributeValues;
}

export interface EditorValues {
  name: string;
  description: string;
  product_type: (typeof PRODUCT_TYPES)[number];
  status: (typeof PRODUCT_STATUSES)[number];
  tax_class_id: string;
  base_uom_id: string;
  category_id: string;
  variants: EditorVariantValues[];
}

/**
 * The product-editor form schema, rebuilt whenever the assigned attribute
 * definitions arrive (the runtime Zod mirror lives inside
 * `variants[i].attribute_values`).
 */
export function buildEditorSchema(attributes: readonly Attribute[]): z.ZodType<EditorValues> {
  return z.object({
    name: z.string().min(1),
    description: z.string(),
    product_type: z.enum(PRODUCT_TYPES),
    status: z.enum(PRODUCT_STATUSES),
    tax_class_id: z.string().min(1),
    base_uom_id: z.string().min(1),
    category_id: z.string().min(1),
    variants: z.array(
      z.object({
        variantId: z.string(),
        sku: z.string().min(1),
        status: z.enum(VARIANT_STATUSES),
        attribute_values: buildVariantAttributesSchema(attributes),
      }),
    ),
  }) as z.ZodType<EditorValues>;
}

/** Canonical decimal-string check for qty / factor inputs (string-only). */
export function decimalStringSchema(i18nKey: string): z.ZodType<string> {
  return z
    .string()
    .min(1)
    .refine(isDecimalString, { params: { i18nKey }, message: "Enter a decimal number" });
}
